/**
 * API易 · HappyHorse 视频生成（经后端 /happyhorse_video 代理）
 * 文档：https://docs.apiyi.com/api-capabilities/happyhorse/overview
 */

import { resolveApiUrl } from './apiBaseUrl'
import { finalizeVideoPromptForApi } from './seedanceVideoApi'

export const HAPPYHORSE_MODEL_IDS = [
  'happyhorse-1.0-t2v',
  'happyhorse-1.0-i2v',
  'happyhorse-1.0-r2v',
  'happyhorse-1.0-video-edit'
]

export function isHappyHorseVideoModel(modelId) {
  const id = String(modelId != null ? modelId : '')
    .trim()
    .toLowerCase()
  return HAPPYHORSE_MODEL_IDS.includes(id)
}

function getAuthHeader() {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || ''
}

function parseJsonSafe(text) {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/** HappyHorse 仅 720P / 1080P；480p 映射为 720p */
function normalizeHappyHorseResolution(res) {
  const s = res != null ? String(res).trim().toLowerCase() : ''
  if (s === '1080p') return '1080p'
  return '720p'
}

function pickTaskIdFromCreateResponse(data) {
  if (!data || typeof data !== 'object') return ''
  for (const k of ['id', 'task_id', 'taskId']) {
    const v = data[k]
    if (v != null && String(v).trim()) return String(v).trim()
  }
  if (data.data && typeof data.data === 'object') {
    for (const k of ['id', 'task_id', 'taskId']) {
      const v = data.data[k]
      if (v != null && String(v).trim()) return String(v).trim()
    }
  }
  if (data.output && typeof data.output === 'object') {
    const v = data.output.task_id || data.output.taskId
    if (v != null && String(v).trim()) return String(v).trim()
  }
  if (data.raw && typeof data.raw === 'object') {
    return pickTaskIdFromCreateResponse(data.raw)
  }
  return ''
}

function normalizeTaskQueryPayload(data) {
  if (!data || typeof data !== 'object') return data
  const inner = data.data && typeof data.data === 'object' ? { ...data, ...data.data } : data
  const videoUrl =
    inner.video_url ||
    inner.result_url ||
    (inner.output && (inner.output.video_url || inner.output.result_url)) ||
    ''
  const status = inner.status != null ? inner.status : data.status
  const id = pickTaskIdFromCreateResponse(inner) || pickTaskIdFromCreateResponse(data)
  return {
    ...inner,
    id: id || inner.id,
    status,
    video_url: videoUrl || inner.video_url,
    result_url: inner.result_url || videoUrl
  }
}

/**
 * @param {Object} opts
 * @param {string} opts.prompt
 * @param {string} opts.model happyhorse-1.0-t2v | i2v | r2v | video-edit
 * @param {number} [opts.duration] 2～15 秒
 * @param {string} [opts.resolution] 720p | 1080p
 * @param {string[]|string} [opts.images] 参考图 / 首帧
 * @param {string} [opts.videoUrl] video-edit 输入视频
 * @param {boolean} [opts.promptExtend]
 * @param {boolean} [opts.watermark]
 * @param {AbortSignal} [opts.signal]
 */
export async function createHappyHorseVideoTask(opts) {
  const {
    prompt,
    model,
    duration,
    resolution,
    images,
    videoUrl,
    promptExtend,
    watermark,
    signal
  } = opts || {}
  const modelId = String(model || 'happyhorse-1.0-t2v').trim()
  if (!isHappyHorseVideoModel(modelId)) {
    throw new Error('不支持的 HappyHorse 模型：' + modelId)
  }
  const imageList = Array.isArray(images)
    ? images.map(x => (x == null ? '' : String(x).trim())).filter(Boolean)
    : images
      ? [String(images).trim()].filter(Boolean)
      : []
  const res = await fetch(resolveApiUrl('/happyhorse_video'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getAuthHeader()
    },
    body: JSON.stringify({
      prompt: finalizeVideoPromptForApi(prompt),
      model: modelId,
      duration:
        duration != null && !Number.isNaN(Number(duration))
          ? Math.min(15, Math.max(2, Math.round(Number(duration))))
          : 5,
      resolution: normalizeHappyHorseResolution(resolution),
      ...(imageList.length ? { images: imageList } : {}),
      ...(videoUrl ? { video_url: String(videoUrl).trim() } : {}),
      ...(promptExtend != null ? { prompt_extend: !!promptExtend } : {}),
      ...(watermark != null ? { watermark: !!watermark } : {})
    }),
    signal
  })
  const text = await res.text()
  const data = parseJsonSafe(text) || { raw: text }
  if (!res.ok) {
    const msg =
      (data && (data.message || data.error || data.errors)) ||
      text.slice(0, 400) ||
      `HTTP ${res.status}`
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }
  const taskId = pickTaskIdFromCreateResponse(data)
  if (!taskId) {
    throw new Error(
      '创建 HappyHorse 任务成功但未解析到 task_id。响应片段：' + text.slice(0, 400)
    )
  }
  return { ...data, id: taskId }
}

export async function getHappyHorseVideoTask(taskId, signal) {
  const id = encodeURIComponent(String(taskId || '').trim())
  if (!id) throw new Error('缺少 task_id')
  const res = await fetch(resolveApiUrl(`/happyhorse_video/${id}`), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getAuthHeader()
    },
    signal
  })
  const text = await res.text()
  const data = parseJsonSafe(text) || {}
  if (!res.ok) {
    const msg = data.message || data.error || text.slice(0, 400) || `HTTP ${res.status}`
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }
  return normalizeTaskQueryPayload(data)
}

export function pickHappyHorseTaskVideoUrl(info) {
  if (!info || typeof info !== 'object') return ''
  const u =
    info.video_url ||
    info.result_url ||
    (info.output && (info.output.video_url || info.output.result_url)) ||
    ''
  return String(u || '').trim()
}
